import { useCallback, useEffect, useRef, useState } from "react";
import {
  View,
  Text,
  Pressable,
  Image,
  Platform,
  TextInput,
  ActivityIndicator,
  type TextStyle,
} from "react-native";
import { BlurView } from "expo-blur";
import * as ImagePicker from "expo-image-picker";
import { Camera, Plus } from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { useToast } from "@louise/moon-ui";
import { EMBER } from "../../../../onboarding/theme";
import type { OnboardingData } from "../../../../onboarding/types";
import { useYouScreen } from "../YouScreenContext";
import { useUpdatePatient } from "@src/packages/patients/hooks/usePatient";
import {
  useGetUploadUrl,
  useUploadFile,
  useCompleteUpload,
} from "@src/packages/documents/hooks/useDocuments";
import { useProfilePictureUrl } from "../../../hooks/useProfilePictureUrl";
import { resolveUploadContentType } from "@src/packages/utils/fileUpload";

const WARM_GRADIENT_STYLE: TextStyle =
  Platform.OS === "web"
    ? {
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='0.06'/%3E%3C/svg%3E"), linear-gradient(135deg, #3B1A08 0%, #7A3E15 35%, #3B1A08 60%, #6B3410 85%, #3B1A08 100%)`,
        backgroundSize: "200px 200px, 100% 100%",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
      }
    : { color: "#3B1A08" };

/**
 * Inline editable name — large gradient text.
 */
function NameField({
  value,
  onSave,
  isSaving,
}: {
  value: string;
  onSave: (v: string) => void;
  isSaving: boolean;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("NameField");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<TextInput>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const handlePress = useCallback(() => {
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const handleBlur = useCallback(() => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) onSave(trimmed);
  }, [draft, value, onSave]);

  if (editing) {
    return (
      <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
        <TextInput
          ref={inputRef}
          className="font-dm-sans"
          value={draft}
          onChangeText={setDraft}
          onBlur={handleBlur}
          onSubmitEditing={() => inputRef.current?.blur()}
          placeholder={t("you.profile_header.add_your_name")}
          placeholderTextColor={EMBER.colors.textFaint}
          autoCapitalize="words"
          returnKeyType="done"
          editable={!isSaving}
          style={{
            fontSize: 26,
            fontWeight: "700",
            color: EMBER.colors.text,
            textAlign: "center",
            padding: 0,
            margin: 0,
            minWidth: 140,
          }}
        />
        {isSaving && (
          <ActivityIndicator size="small" color={EMBER.colors.textFaint} />
        )}
      </View>
    );
  }

  if (value) {
    return (
      <Pressable
        testID="profile-name-field"
        onPress={() => {
          capturePress(pressLabel(value), "profile_name_field");
          handlePress();
        }}
        hitSlop={12}
        disabled={isSaving}
      >
        <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <Text
            className="font-dm-sans"
            style={[
              {
                fontSize: 26,
                fontWeight: "700",
                color: EMBER.colors.text,
                textAlign: "center",
                letterSpacing: -0.3,
              },
              WARM_GRADIENT_STYLE,
            ]}
          >
            {value}
          </Text>
          {isSaving && (
            <ActivityIndicator size="small" color={EMBER.colors.textFaint} />
          )}
        </View>
      </Pressable>
    );
  }

  return (
    <Pressable
      testID="profile-name-field"
      onPress={() => {
        capturePress(
          pressLabel(t("you.profile_header.add_your_name")),
          "you.profile_header.add_your_name",
        );
        handlePress();
      }}
      disabled={isSaving}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 4,
          paddingHorizontal: 14,
          paddingVertical: 8,
          borderRadius: 999,
          borderWidth: 1,
          borderColor: EMBER.colors.border,
          borderStyle: "dashed",
        }}
      >
        <Plus size={12} color={EMBER.colors.textFaint} strokeWidth={2} />
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            fontWeight: "500",
            color: EMBER.colors.textLight,
          }}
        >
          {t("you.profile_header.add_your_name")}
        </Text>
      </View>
    </Pressable>
  );
}

type NameSaveStatus = "idle" | "saving";

type Props = {
  data: OnboardingData;
  avatarOnly?: boolean;
  infoOnly?: boolean;
};

export default function ProfileHeader({ data, avatarOnly, infoOnly }: Props) {
  const t = useTranslations();
  const { showToast } = useToast();
  const { patient } = useYouScreen();
  const capturePress = usePressCapture("ProfileHeader");
  const updateName = useUpdatePatient();
  const updateAvatar = useUpdatePatient();
  const getUploadUrl = useGetUploadUrl();
  const uploadFile = useUploadFile();
  const completeUpload = useCompleteUpload();
  const [uploading, setUploading] = useState(false);
  // The picked image, shown immediately after upload for instant feedback.
  const [localAvatarUri, setLocalAvatarUri] = useState<string | null>(null);
  // Presigned URL for the saved profile picture (shared with the top-right
  // avatar button so both stay in sync).
  const remoteAvatarUrl = useProfilePictureUrl();
  const [nameSaveStatus, setNameSaveStatus] = useState<NameSaveStatus>("idle");
  const pendingNameRef = useRef<string | null>(null);

  const profilePictureUrl = localAvatarUri ?? remoteAvatarUrl;
  const firstName =
    pendingNameRef.current ?? patient?.firstName ?? data.firstName ?? "";

  const uploadAvatar = useCallback(
    async (asset: ImagePicker.ImagePickerAsset) => {
      setUploading(true);

      try {
        const contentType = resolveUploadContentType({
          name: "profile-picture.jpg",
          uri: asset.uri,
          mimeType: asset.mimeType,
        });
        const uploadUrlResult = await getUploadUrl.mutateAsync({
          name: "profile-picture.jpg",
          contentType,
        });

        const response = await fetch(asset.uri);
        if (!response.ok) throw new Error("Profile picture source unavailable");
        const blob = await response.blob();

        await uploadFile.mutateAsync({
          uploadUrl: uploadUrlResult.uploadUrl,
          file: blob,
          contentType,
        });

        await completeUpload.mutateAsync(uploadUrlResult.uploadId);

        await updateAvatar.mutateAsync({
          profilePictureId: uploadUrlResult.uploadId,
        });

        // Show the picked image right away; the persisted presigned URL
        // hydrates on the next patient refetch.
        setLocalAvatarUri(asset.uri);
      } catch {
        // Keep the previous avatar in place and retain the asset for retry.
        // Do not include the asset URI or API error in telemetry.
        console.error("Profile picture upload failed");
        showToast({
          variant: "error",
          message: t("you.profile_header.avatar_upload_failed"),
        });
      } finally {
        setUploading(false);
      }
    },
    [completeUpload, getUploadUrl, showToast, t, updateAvatar, uploadFile],
  );

  const handlePickAvatar = useCallback(async () => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      });

      if (result.canceled || !result.assets[0]) return;

      const asset = result.assets[0];
      await uploadAvatar(asset);
    } catch {
      console.error("Profile picture picker failed");
      showToast({
        variant: "error",
        message: t("you.profile_header.avatar_upload_failed"),
      });
    }
  }, [showToast, t, uploadAvatar]);

  const avatarImage = profilePictureUrl ? (
    <Image
      source={{ uri: profilePictureUrl }}
      style={{ width: 70, height: 70, borderRadius: 35 }}
      resizeMode="cover"
    />
  ) : (
    <Image
      source={require("../../../../assets/images/avatar-placeholder.png")}
      style={{ width: 70, height: 70, borderRadius: 35 }}
      resizeMode="contain"
    />
  );

  const avatarContainerStyle = {
    width: 80,
    height: 80,
    borderRadius: 40,
    alignItems: "center" as const,
    justifyContent: "center" as const,
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.5)",
    overflow: "hidden" as const,
  };

  const [hovered, setHovered] = useState(false);

  const handleSaveName = useCallback(
    (newName: string) => {
      pendingNameRef.current = newName;
      setNameSaveStatus("saving");

      updateName.mutate(
        { firstName: newName },
        {
          onSuccess: () => {
            pendingNameRef.current = null;
            setNameSaveStatus("idle");
          },
          onError: () => {
            pendingNameRef.current = newName;
            setNameSaveStatus("idle");
            console.error("Profile name update failed");
            showToast({
              variant: "error",
              message: t("you.profile_header.name_save_failed"),
            });
          },
        },
      );
    },
    [showToast, t, updateName],
  );

  const cameraOverlay = (hovered || uploading) && (
    <View
      style={{
        position: "absolute",
        width: 80,
        height: 80,
        borderRadius: 40,
        backgroundColor: "rgba(0,0,0,0.35)",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {uploading ? (
        <ActivityIndicator size="small" color="#FFFFFF" />
      ) : (
        <Camera size={20} color="#FFFFFF" strokeWidth={1.5} />
      )}
    </View>
  );

  const avatar = (
    <Pressable
      style={{ width: 80, height: 80 }}
      onPress={() => {
        // Image-only control with no visible copy to name it.
        capturePress("profile_avatar");
        void handlePickAvatar();
      }}
      disabled={uploading}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
    >
      {Platform.OS === "web" ? (
        <View
          style={{
            ...avatarContainerStyle,
            backgroundColor: "rgba(255, 255, 255, 0.4)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
          }}
        >
          {avatarImage}
          {cameraOverlay}
        </View>
      ) : (
        <BlurView intensity={60} tint="light" style={avatarContainerStyle}>
          {avatarImage}
          {cameraOverlay}
        </BlurView>
      )}
    </Pressable>
  );

  if (avatarOnly) {
    return <View style={{ alignItems: "center", zIndex: 1 }}>{avatar}</View>;
  }

  const info = (
    <>
      <View style={{ marginBottom: 8 }}>
        <NameField
          value={firstName}
          onSave={handleSaveName}
          isSaving={nameSaveStatus === "saving" || updateName.isPending}
        />
      </View>
    </>
  );

  if (infoOnly) {
    return (
      <View style={{ alignItems: "center", marginBottom: 20 }}>{info}</View>
    );
  }

  return (
    <View style={{ alignItems: "center", marginBottom: 28 }}>
      <View style={{ marginBottom: 16 }}>{avatar}</View>
      {info}
    </View>
  );
}
